// =============================================================================
// PhotoUploader — the ONE photo-upload control (S100 Build 1, shared in Build 2)
// =============================================================================
// Extracted from RecruitEdit.jsx when Build 2 gave it a second consumer
// (MyProfile). The ENDPOINT is a prop because the two surfaces authenticate
// differently — staff post to /api/recruits/:id/photo, a player posts to
// /api/me/photo and the server works out who they are from the JWT. Everything
// else — processing, both buckets, the pointer write — happens server-side in
// one shared pipeline; this component only picks a file and shows the result.
//
// On success the SERVER has already written the pointer; the caller's
// onUploaded mirrors the returned URL into local state and must NOT schedule
// an autosave of the same value (see RecruitEdit's call site).

import { useRef, useState } from 'react';
import { api } from '../lib/api.js';

export default function PhotoUploader({ endpoint, url, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = '';                          // same file re-pickable after an error
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('That photo is over 10 MB. Please use a smaller photo.');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const body = await api.upload(endpoint, fd);
      if (!body?.url) throw new Error('Upload succeeded but no URL came back.');
      onUploaded(body.url);
    } catch (err) {
      setError(err.body?.error || err.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex items-center gap-4">
      {url ? (
        <img src={url} alt="Profile" className="w-20 h-20 rounded object-cover border border-gray-200" />
      ) : (
        <div className="w-20 h-20 rounded bg-gray-100 border border-gray-200 flex items-center justify-center text-xs text-silver-mute">
          No photo
        </div>
      )}
      <div>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={onPick}
        />
        <button
          type="button"
          className="btn-secondary"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? 'Uploading…' : url ? 'Replace photo' : 'Upload photo'}
        </button>
        <p className="text-xs text-silver-mute mt-1">JPG, PNG, or WebP up to 10 MB. Location data is removed automatically.</p>
        {error && <p className="text-xs text-red-700 mt-1">{error}</p>}
      </div>
    </div>
  );
}
